import { clipPlaceholderAperture, type RawSlotMeta } from '@frameShapes'
import { normalizeBlendMode } from '@renderer/utils/filterUtils'
import type { FrameOption } from '@renderer/types/configState'

type DrawParams = FrameOption['drawParams']

/** Where a photograph will go: plain grey, so the print shows the window's edges. */
const WINDOW_FILL = '#D4D4D4'
const WINDOW_INK = '#6B6B6B'

/**
 * The frame itself, as it prints, for the printer settings' test print: the
 * frame's artwork at full print size, every photo window drawn empty in its
 * real shape with its number, the QR spot marked.
 *
 * A frame's `thumbnail` is only a picture for lists and pickers, and it need
 * not match the print — Bingkai Kenangan's 6x8 sheets show a single strip — so
 * a test print made from it could not check what a printer really does with
 * the frame (paper size, margins, a perforation line). This draws from
 * `drawParams`, layer by layer in the same order and with the same aperture,
 * rotation, opacity and blend rules as PrintPreview.
 *
 * A layer whose image cannot load is left out rather than failing the test.
 */
export async function renderFrameTestImage(drawParams: DrawParams): Promise<string> {
  const { canvasWidth: W, canvasHeight: H } = drawParams
  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('no 2d context')

  ctx.fillStyle = drawParams.backgroundColor || '#FFFFFF'
  ctx.fillRect(0, 0, W, H)

  const art = await Promise.all(
    drawParams.images.map((layer) =>
      layer.type === 'image' && layer.src ? loadImage(layer.src).catch(() => null) : null
    )
  )

  drawParams.images.forEach((layer, i) => {
    ctx.save()
    ctx.globalAlpha = (layer.opacity ?? 100) / 100
    ctx.globalCompositeOperation = normalizeBlendMode(layer.blendMode)
    const cx = layer.left + layer.width / 2
    const cy = layer.top + layer.height / 2
    if (layer.type.startsWith('photo-')) {
      ctx.translate(cx, cy)
      if (layer.rotation) ctx.rotate(layer.rotation)
      clipPlaceholderAperture(
        ctx,
        layer.width,
        layer.height,
        (layer as { slotMeta?: RawSlotMeta }).slotMeta
      )
      ctx.fillStyle = WINDOW_FILL
      ctx.fillRect(-layer.width / 2, -layer.height / 2, layer.width, layer.height)
      ctx.fillStyle = WINDOW_INK
      ctx.font = `600 ${Math.round(Math.min(layer.width, layer.height) * 0.28)}px sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(layer.type.replace('photo-', ''), 0, 0)
    } else if (layer.type === 'qr') {
      if (layer.rotation) {
        ctx.translate(cx, cy)
        ctx.rotate(layer.rotation)
        ctx.translate(-cx, -cy)
      }
      ctx.fillStyle = WINDOW_FILL
      ctx.fillRect(layer.left, layer.top, layer.width, layer.height)
      ctx.fillStyle = WINDOW_INK
      ctx.font = `600 ${Math.round(Math.min(layer.width, layer.height) * 0.3)}px sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('QR', cx, cy)
    } else if (art[i]) {
      if (layer.rotation) {
        ctx.translate(cx, cy)
        ctx.rotate(layer.rotation)
        ctx.translate(-cx, -cy)
      }
      ctx.drawImage(art[i] as HTMLImageElement, layer.left, layer.top, layer.width, layer.height)
    }
    ctx.restore()
  })

  return canvas.toDataURL('image/jpeg', 0.95)
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    // Frame art comes from the CDN; without CORS the canvas could not export.
    img.crossOrigin = 'anonymous'
    img.onload = (): void => resolve(img)
    img.onerror = (): void => reject(new Error(`frame layer failed to load: ${src}`))
    img.src = src
  })
}
